import React, {
  useState, useCallback, useMemo, createContext,
} from 'react';
import PropTypes from 'prop-types';
import { getToggleState, setToggleState } from 'core/localStore';

const TOUR_COMPLETED_KEY = 'cricket-interactive-tour-completed';
const TOURNAMENT_TOUR_COMPLETED_KEY = 'cricket-tournament-tour-completed';

const tourCompletedKeys = {
  main: TOUR_COMPLETED_KEY,
  tournament: TOURNAMENT_TOUR_COMPLETED_KEY,
};

export const TourContext = createContext({
  isTourRunning: false,
  tourStepIndex: 0,
  isTourCompleted: false,
  isTournamentTourCompleted: false,
  activeTourId: null,
  startTour: () => {},
  stopTour: () => {},
  nextStep: () => {},
  prevStep: () => {},
});

export function TourContextProvider({ children }) {
  const [isTourRunning, setIsTourRunning] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  const [activeTourId, setActiveTourId] = useState(null);
  const [isTourCompleted, setIsTourCompleted] = useState(
    () => getToggleState(TOUR_COMPLETED_KEY, false),
  );
  const [isTournamentTourCompleted, setIsTournamentTourCompleted] = useState(
    () => getToggleState(TOURNAMENT_TOUR_COMPLETED_KEY, false),
  );

  const markTourCompleted = useCallback((tourId) => {
    const storageKey = tourCompletedKeys[tourId];
    if (!storageKey) return;
    setToggleState(storageKey, true);
    if (tourId === 'main') setIsTourCompleted(true);
    if (tourId === 'tournament') setIsTournamentTourCompleted(true);
  }, []);

  const startTour = useCallback((tourId = 'main') => {
    setActiveTourId(tourId);
    setTourStepIndex(0);
    setIsTourRunning(true);
  }, []);

  const stopTour = useCallback((markComplete = true) => {
    setIsTourRunning(false);
    setTourStepIndex(0);
    if (markComplete) markTourCompleted(activeTourId);
    setActiveTourId(null);
  }, [activeTourId, markTourCompleted]);

  const nextStep = useCallback((totalSteps) => {
    setTourStepIndex((prev) => {
      if (prev >= totalSteps - 1) {
        setIsTourRunning(false);
        markTourCompleted(activeTourId);
        setActiveTourId(null);
        return 0;
      }
      return prev + 1;
    });
  }, [activeTourId, markTourCompleted]);

  const prevStep = useCallback(() => {
    setTourStepIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const value = useMemo(() => ({
    isTourRunning,
    tourStepIndex,
    isTourCompleted,
    isTournamentTourCompleted,
    activeTourId,
    startTour,
    stopTour,
    nextStep,
    prevStep,
  }), [
    isTourRunning, tourStepIndex, isTourCompleted, isTournamentTourCompleted,
    activeTourId, startTour, stopTour, nextStep, prevStep,
  ]);

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
}

TourContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
